import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Services from './services/Services';

const ThemeContext = createContext();

const THEME_KEY_TO_CLASS = {
  root: 'root',
  light: 'light-theme',
  tron: 'tron-theme',
  dark: 'dark-theme',
};

const THEME_CLASS_TO_KEY = {
  'root': 'root',
  'light-theme': 'light',
  'tron-theme': 'tron',
  'dark-theme': 'dark',
};

function normalizeInitialTheme() {
  const stored = localStorage.getItem('app-theme');
  if (!stored) return 'root';
  if (THEME_CLASS_TO_KEY[stored]) return THEME_CLASS_TO_KEY[stored];
  if (THEME_KEY_TO_CLASS[stored]) return stored;
  return 'root';
}

export const ThemeProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('token') || sessionStorage.getItem('token') || null);

  const [themeKey, setThemeKey] = useState(() => normalizeInitialTheme());

  const isBootstrappingRef = useRef(true);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token') setToken(e.newValue || sessionStorage.getItem('token') || null);
    };

    const onFocus = () => {
      const latest = localStorage.getItem('token') || sessionStorage.getItem('token');
      setToken(latest || null);
    };

    const onCustom = () => {
      const latest = localStorage.getItem('token') || sessionStorage.getItem('token');
      setToken(latest || null);
    };

    let bc;
    try {
      bc = new BroadcastChannel('auth');
      bc.onmessage = (ev) => {
        if (ev?.data?.type === 'token') {
          setToken(ev.data.value || null);
        } else if (ev?.data?.type === 'LOGIN' && ev?.data?.token) {
          setToken(ev.data.token || null);
        }
      };
    } catch (_) {
      // Safari private mode vb. durumlar için sessiz düş
    }

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    window.addEventListener('app-token-changed', onCustom);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('app-token-changed', onCustom);
      if (bc) bc.close();
    };
  }, []);

  // DOM class yönetimi + localStorage eşitleme
  useEffect(() => {
    const rootEl = document.documentElement;
    rootEl.classList.remove('light-theme', 'tron-theme', 'dark-theme');

    const themeClass = THEME_KEY_TO_CLASS[themeKey] || 'root';
    if (themeClass !== 'root') {
      rootEl.classList.add(themeClass);
    }

    localStorage.setItem('app-theme', themeKey);
  }, [themeKey]);

  // Açılışta kullanıcı ayarlarını çek ve themeKey'i sunucuya göre ayarla
  useEffect(() => {
    let cancelled = false;
    async function bootstrapTheme() {
      if (!token) {
        isBootstrappingRef.current = false;
        return;
      }
      try {
        const res = await Services.GetUserSettings(token);
        const serverThemeKey =
          res?.data?.status && res?.data?.result?.webTheme
            ? String(res.data.result.webTheme)
            : null;

        if (!cancelled) {
          if (serverThemeKey && THEME_KEY_TO_CLASS[serverThemeKey]) {
            setThemeKey(serverThemeKey);
          }
        }
      } catch (e) {
        console.error('GetUserSettings error:', e);
      } finally {
        if (!cancelled) {
          isBootstrappingRef.current = false;
        }
      }
    }

    bootstrapTheme();
    return () => {
      cancelled = true;
    };
  }, [token]); // ❗️ARTIK STATE'TEKİ token'I İZLİYOR

  // Kullanıcı tema değiştirdiğinde değişikliği server’a yaz
  useEffect(() => {
    if (!token) return;
    if (isBootstrappingRef.current) return;

    let cancelled = false;

    async function persistTheme() {
      try {
        await Services.UpdateUserTheme(token, themeKey);
      } catch (e) {
        console.error('UpdateUserTheme error:', e);
      }
    }

    Promise.resolve().then(() => {
      if (!cancelled) persistTheme();
    });

    return () => {
      cancelled = true;
    };
  }, [themeKey, token]);

  const value = useMemo(
    () => ({
      theme: THEME_KEY_TO_CLASS[themeKey],
      themeKey,
      setTheme: (next) => {
        let nextKey = next;
        if (THEME_CLASS_TO_KEY[next]) {
          nextKey = THEME_CLASS_TO_KEY[next];
        }
        if (!THEME_KEY_TO_CLASS[nextKey]) nextKey = 'root';
        setThemeKey(nextKey);
      },
      setThemeKey,
    }),
    [themeKey]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);